<template>
  <woot-modal :show.sync="isOpen" :on-close="onClose" size="medium">
    <div class="modal-wrap">
      <div class="modal-header">
        <h3 class="modal-title">{{ title }}</h3>
        <p class="modal-subtitle">{{ records.length }} record(s)</p>
      </div>

      <div class="modal-body">
        <div v-if="isLoading" class="loader">
          <spinner />
        </div>
        <empty-state v-else-if="!records.length" title="No records found" />
        <template v-else>
          <div v-if="agentBreakdown.length" class="agent-breakdown">
            <div
              v-for="agent in agentBreakdown"
              :key="agent.name"
              class="agent-breakdown-item"
            >
              <span class="agent-breakdown-name">{{ agent.name }}</span>
              <span class="agent-breakdown-count">{{ agent.count }}</span>
            </div>
          </div>
          <ul class="record-list">
            <li v-for="record in records" :key="record.id" class="record-item">
              <div class="record-header">
                <div class="contact-info">
                  <span class="contact-name">
                    {{ record.contact.name || '-' }}
                  </span>
                  <span class="contact-phone">
                    {{ record.contact.phone_number || '' }}
                  </span>
                </div>
                <Label
                  :title="record.result"
                  :color-scheme="getResultColor(record.result)"
                />
              </div>
              <p v-if="record.summary" class="record-summary">
                {{ record.summary.summary_text }}
              </p>
            </li>
          </ul>
        </template>
      </div>
    </div>
  </woot-modal>
</template>

<script>
import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Label from 'dashboard/components/ui/Label';

const RESULT_COLOR_MAP = {
  Good: 'success',
  Success: 'primary',
  Bad: 'alert',
  Reject: 'warning',
};

export default {
  components: { Spinner, EmptyState, Label },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    records: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    agentBreakdown() {
      const map = {};
      this.records.forEach(r => {
        const name = r.llm_chatbot?.name || 'Unknown';
        map[name] = (map[name] || 0) + 1;
      });
      return Object.entries(map)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    getResultColor(result) {
      return RESULT_COLOR_MAP[result] || 'secondary';
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-wrap {
  display: flex;
  flex-direction: column;
  max-height: 80vh;
}

.modal-header {
  padding: var(--space-normal) var(--space-large);
  border-bottom: 1px solid var(--s-75);
  flex-shrink: 0;

  .modal-title {
    font-size: var(--font-size-medium);
    font-weight: var(--font-weight-bold);
    color: var(--s-900);
  }

  .modal-subtitle {
    font-size: var(--font-size-small);
    color: var(--s-500);
    margin-top: var(--space-smaller);
    margin-bottom: 0;
  }
}

.modal-body {
  padding: var(--space-normal) var(--space-large);
  overflow-y: auto;
  flex: 1;
}

.loader {
  display: flex;
  justify-content: center;
  padding: var(--space-large);
}

.agent-breakdown {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-smaller);
  padding: var(--space-small) var(--space-normal);
  background-color: var(--s-25);
  border: 1px solid var(--s-75);
  border-radius: var(--border-radius-normal);
  margin-bottom: var(--space-normal);
}

.agent-breakdown-item {
  display: flex;
  align-items: center;
  gap: var(--space-smaller);
  font-size: var(--font-size-small);
  background-color: var(--white);
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius-normal);
  padding: 2px var(--space-small);
}

.agent-breakdown-name {
  color: var(--s-700);
}

.agent-breakdown-count {
  font-weight: var(--font-weight-bold);
  color: var(--w-600);
  background-color: var(--w-25);
  border-radius: 10px;
  padding: 0 var(--space-smaller);
}

.record-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-normal);
}

.record-item {
  border: 1px solid var(--s-75);
  border-radius: var(--border-radius-normal);
  padding: var(--space-normal);
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--space-smaller);
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 2px;

  .contact-name {
    font-weight: var(--font-weight-medium);
    font-size: var(--font-size-small);
    color: var(--s-900);
  }

  .contact-phone {
    font-size: var(--font-size-mini);
    color: var(--s-500);
  }
}

.record-summary {
  font-size: var(--font-size-small);
  color: var(--s-600);
  line-height: 1.5;
  margin: 0;
}
</style>
